import { useEffect, useRef } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { AuthenticatedRoutes } from '@/router/routes';

import { mapBillDetailsToUpdateFormValues, mapUpdateBillFormToRequest } from '../mappers';
import { BillAllowedAction } from '../types';
import { getBillTotal } from '../utils';
import {
  BillValidationIssueCode,
  UPDATE_BILL_MESSAGE_BY_ISSUE,
  UpdateBillFormValues,
  updateBillValidationSchema,
} from '../validations';
import { useBillDetails } from './use-bill-details';
import { useBillMonthlySalaryAvailability } from './use-bill-monthly-salary-availability';
import { useUpdateBill } from './use-update-bill';

interface BillDetailsLocationState {
  billsSearch?: string;
}

const EMPTY_FORM_VALUES: UpdateBillFormValues = {
  monthlySalaryEnabled: false,
  period: { from: undefined, to: undefined },
  dueDate: undefined,
  currencyCode: '',
  items: [],
};

/** Coordinates bill details loading, editing, and safe return to the filtered list. */
export const useBillDetailsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { billId: billIdParam } = useParams<{ billId: string }>();
  const hydratedBillIdRef = useRef<number | null>(null);
  const parsedBillId = Number(billIdParam);
  const billId = Number.isInteger(parsedBillId) && parsedBillId > 0 ? parsedBillId : undefined;
  const query = useBillDetails(billId);
  const updateMutation = useUpdateBill();
  const locationState = (location.state as BillDetailsLocationState | null) ?? null;
  const form = useForm<UpdateBillFormValues>({
    resolver: zodResolver(updateBillValidationSchema),
    mode: 'onChange',
    criteriaMode: 'all',
    defaultValues: EMPTY_FORM_VALUES,
  });
  const isReadOnly = !query.data?.allowedActions.includes(BillAllowedAction.Edit);
  const period = useWatch({ control: form.control, name: 'period' });
  const currencyCode = useWatch({ control: form.control, name: 'currencyCode' });
  const items = useWatch({ control: form.control, name: 'items' });
  const monthlySalaryEnabled = useWatch({ control: form.control, name: 'monthlySalaryEnabled' });
  const monthlySalaryAvailability = useBillMonthlySalaryAvailability({
    memberId: query.data?.member.id,
    startDate: period?.from,
    endDate: period?.to,
    currencyCode,
  });
  const resolvedMonthlySalaryAvailability =
    monthlySalaryAvailability.data ?? query.data?.monthlySalaryAvailability;
  const isTotalNegative =
    getBillTotal({
      items,
      monthlySalaryEnabled,
      monthlySalaryAmount: resolvedMonthlySalaryAvailability?.monthlySalaryAmount ?? null,
    }) < 0;

  useEffect(() => {
    if (!query.data) return;
    if (hydratedBillIdRef.current !== query.data.id || !form.formState.isDirty) {
      form.reset(mapBillDetailsToUpdateFormValues(query.data));
      if (!isReadOnly) void form.trigger();
      hydratedBillIdRef.current = query.data.id;
    }
  }, [form, isReadOnly, query.data]);

  const cancelEditing = () => {
    if (query.data) form.reset(mapBillDetailsToUpdateFormValues(query.data));
  };
  const unsavedChanges = useUnsavedChangesGuard({
    isDirty: !isReadOnly && form.formState.isDirty,
    onDiscard: cancelEditing,
  });
  const navigateToBills = () =>
    navigate(
      { pathname: AuthenticatedRoutes.Bills, search: locationState?.billsSearch ?? '' },
      { replace: true, state: null }
    );
  const navigateBack = () => {
    const action = () => {
      if (locationState?.billsSearch !== undefined || location.key === 'default') navigateToBills();
      else navigate(-1);
    };
    if (unsavedChanges.checkUnsavedChanges(action)) action();
  };
  const cancelEdit = () => {
    if (unsavedChanges.checkUnsavedChanges(cancelEditing)) cancelEditing();
  };
  const submit = form.handleSubmit(values => {
    if (!billId || !query.data) return;

    const total = getBillTotal({
      items: values.items,
      monthlySalaryEnabled: values.monthlySalaryEnabled,
      monthlySalaryAmount: resolvedMonthlySalaryAvailability?.monthlySalaryAmount ?? null,
    });
    if (total < 0) {
      form.setError('items', {
        type: 'manual',
        message: UPDATE_BILL_MESSAGE_BY_ISSUE[BillValidationIssueCode.NegativeTotal],
      });
      return;
    }

    updateMutation.mutate(
      {
        billId,
        request: mapUpdateBillFormToRequest({
          details: query.data,
          values,
          monthlySalaryAmount: resolvedMonthlySalaryAvailability?.monthlySalaryAmount ?? null,
        }),
      },
      {
        onSuccess: () => {
          form.reset(values);
          unsavedChanges.allowNavigation();
          navigateToBills();
        },
      }
    );
  });

  return {
    details: query.data,
    form,
    isLoading: query.isLoading,
    isError: query.isError,
    isReadOnly,
    isUpdating: updateMutation.isPending,
    isTotalNegative,
    monthlySalaryAvailability: resolvedMonthlySalaryAvailability,
    isMonthlySalaryAvailabilityLoading: monthlySalaryAvailability.isFetching,
    navigateBack,
    cancelEdit,
    submit,
    unsavedChanges,
  };
};
